// ✅ ALTERED ONLY FOR THEME (to match your “ReportStudentMarks” theme)
// - Page wrapper BG #F2F2F2
// - Root paper: rounded + subtle blue border
// - Orange gradient header bar
// - Sections use: white surface + soft blue border + LEFT ORANGE ACCENT STRIPE
// - Inputs/autocomplete: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: BLUE header, zebra rows, hover tint, subtle borders
// - No business/API logic changed (only styling + layout tidy)

import React, { useEffect, useState, memo, useMemo } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Autocomplete,
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  useMediaQuery,
  LinearProgress,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { API_URL, API_UPLOAD_URL } from "./config";

// ---- Moved OUTSIDE so its identity is stable across renders ----
const READ_ONLY_FIELDS = new Set([
  "DocumentsReturnedTo",
  "DocumentsReturnedOn",
  "DocumentsReturnedBy",
]);

const EditableField = memo(function EditableField({
  label,
  fieldName,
  value,
  editMode,
  onChange,
  inputSx,
}) {
  const isDate = fieldName === "AdmissionDate" || fieldName === "DateOfBirth";
  const [local, setLocal] = React.useState(value ?? "");

  React.useEffect(() => {
    setLocal(value ?? "");
  }, [value]);

  const commit = React.useCallback(() => {
    const v = isDate && typeof local === "string" ? local.slice(0, 10) : local;
    onChange(v);
  }, [isDate, local, onChange]);

  //if (editMode && fieldName !== "StudentID" && fieldName !== "Username") {
  
  const isReadOnly = fieldName === "StudentID" || fieldName === "Username" || READ_ONLY_FIELDS.has(fieldName);
  if (editMode && !isReadOnly) {
    return (
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          type={isDate ? "date" : "text"}
          name={fieldName}
          value={
            isDate
              ? typeof local === "string"
                ? local.slice(0, 10)
                : local
              : local ?? ""
          }
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          sx={inputSx}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
      >
        {value ?? "-"}
      </Typography>
    </Box>
  );
});
// ----------------------------------------------------------------

const ReportStudentPersonalInformation = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const COLS_XL = 6;

  // ✅ Theme constants (match ReportStudentMarks)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: isSmDown ? "flex-start" : "center",
    justifyContent: "space-between",
    gap: 2,
    flexDirection: isSmDown ? "column" : "row",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const tableWrapSx = { ...surfaceSx, overflow: "hidden" };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
  });

  const [data, setData] = useState([]);
  const [editedRecord, setEditedRecord] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [studentList, setStudentList] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [shouldSearch, setShouldSearch] = useState(false);
  const [logincampusCode, setCampusCode] = useState("");
  const [passportPreview, setPassportPreview] = useState(null);
  const [originalPhotoFilename, setOriginalPhotoFilename] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [allFilteredStudents, setAllFilteredStudents] = useState([]);
  const [editMode, setEditMode] = useState(false);

  // photo edit states
  const [photoFile, setPhotoFile] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [photoError, setPhotoError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [guardianImageFiles, setGuardianImageFiles] = useState({});

  const token = localStorage.getItem("auth");

  // dropdown filters and options
  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [loadingLookups, setLoadingLookups] = useState(false);

  // students list based on dropdowns
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // Get campus code from token
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, [token]);

  // fetch dropdown lookups
  useEffect(() => {
    if (!logincampusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authHeader,
          params: { CampusCode: logincampusCode },
        });
        if (cancelled) return;
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
        });
      } catch (e) {
        console.error("Failed to load dropdowns:", e);
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [logincampusCode, authHeader]);

  // Update passport preview when student changes
  useEffect(() => {
    if (selectedStudent?.PassportPhoto) {
      const url = `${API_UPLOAD_URL}/uploads/${selectedStudent.PassportPhoto}`;
      setPassportPreview(url);
      setOriginalPhotoFilename(selectedStudent.PassportPhoto);
    } else {
      setPassportPreview(null);
      setOriginalPhotoFilename(null);
    }
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    setObjectUrl(null);
    setPhotoFile(null);
    setPhotoError("");
  }, [selectedStudent]); // eslint-disable-line

  // Cleanup object URL
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  // Fetch student list for auto-complete search (simple debounce)
  useEffect(() => {
    const t = setTimeout(async () => {
      if (searchTerm.length < 3 || !logincampusCode) {
        setStudentList([]);
        return;
      }
      try {
        const response = await axios.get(
          `${API_URL}/api/studentList?search=${encodeURIComponent(searchTerm)}&CampusCode=${encodeURIComponent(
            logincampusCode
          )}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStudentList(response.data || []);
      } catch (error) {
        console.error("Error fetching student list:", error);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, logincampusCode, token]);

  // Fetch detailed report data (drill-down)
  useEffect(() => {
    const fetchData = async () => {
      if (!shouldSearch || !selectedStudent || !logincampusCode) {
        setData([]);
        return;
      }

      try {
        const params = {
          search: selectedStudent.StudentID,
          CampusCode: logincampusCode,
        };

        if (filters.SubCampusCode.length) params.SubCampusCode = filters.SubCampusCode;
        if (filters.DegreeCode.length) params.DegreeCode = filters.DegreeCode;
        if (filters.BranchCode.length) params.BranchCode = filters.BranchCode;
        if (filters.SemesterCode.length) params.SemesterCode = filters.SemesterCode;

        const response = await axios.get(`${API_URL}/api/dataReportStudentPersonalInformationUpdate`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });

        if (Array.isArray(response.data) && response.data.length > 0) {
          const first = response.data[0];     
          setData(response.data);
          setEditedRecord({
            ...first,
            EducationDetails: first.EducationDetails || [],
            CompetitiveExams: first.CompetitiveExams || [],
            DocumentDetails: first.DocumentDetails || [],
          });

          if (response.data[0]?.PassportPhoto) {
            setOriginalPhotoFilename(response.data[0].PassportPhoto);
            setPassportPreview(`${API_UPLOAD_URL}/uploads/${response.data[0].PassportPhoto}`);
          }
        } else {
          setData([]);
          setEditedRecord(null);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 401) navigate("/");
      }
    };
    fetchData();
  }, [shouldSearch, selectedStudent, logincampusCode, filters, token, navigate]);

  const anyFilterSelected =
    (filters.SubCampusCode?.length || 0) > 0 ||
    (filters.DegreeCode?.length || 0) > 0 ||
    (filters.BranchCode?.length || 0) > 0 ||
    (filters.SemesterCode?.length || 0) > 0;

  const handleLoadStudents = async () => {
    setListError("");
    setFilteredStudents([]);
    if (!logincampusCode) return;
    if (!anyFilterSelected) {
      setListError("Select at least one dropdown filter to load students.");
      return;
    }

    try {
      setLoadingList(true);
      const params = { CampusCode: logincampusCode };
      if (filters.SubCampusCode.length) {params.SubCampusCode = filters.SubCampusCode.join(",");}
      if (filters.DegreeCode.length) {params.DegreeCode = filters.DegreeCode.join(",");}
      if (filters.BranchCode.length) {params.BranchCode = filters.BranchCode.join(",");}
      if (filters.SemesterCode.length) {params.SemesterCode = filters.SemesterCode.join(",");}

      const { data } = await axios.get(`${API_URL}/api/dataReportStudentPersonalInformationList`, {
        ...authHeader,
        params,
      });

      const arr = Array.isArray(data) ? data : [];
      setFilteredStudents(arr);
      setAllFilteredStudents(arr);
      setIsCollapsed(false);
      if (!arr.length) setListError("No students found for the selected filters.");
    } catch (e) {
      console.error("Error loading student list:", e);
      setListError("Failed to load students. Please try again.");
    } finally {
      setLoadingList(false);
    }
  };

  const handleSearch = () => {
    setShouldSearch(!!selectedStudent);
    if (!selectedStudent) {
      setData([]);
      setEditedRecord(null);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedRecord((prev) => ({ ...prev, [field]: value }));
  };

  // --- Photo handlers ---
  const fileInputRef = React.useRef(null);

  const handlePickPhoto = () => {
    if (!editMode) return;
    fileInputRef.current?.click();
  };

  const handlePhotoSelect = (e) => {
    setPhotoError("");
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      setPhotoError("Only JPEG and PNG images are allowed.");
      return;
    }
    const maxBytes = 3000 * 1024; // 3MB
    if (file.size > maxBytes) {
      setPhotoError("File must be ≤ 3 MB.");
      return;
    }

    const url = URL.createObjectURL(file);
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    setObjectUrl(url);
    setPhotoFile(file);
    setPassportPreview(url);
  };

  const handleGuardianImageSelect = (n, e) => {
    setPhotoError("");

    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      setPhotoError("Only JPEG and PNG images are allowed.");
      return;
    }

    const maxBytes = 3000 * 1024;
    if (file.size > maxBytes) {
      setPhotoError("File must be ≤ 3 MB.");
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    setGuardianImageFiles((prev) => {
      if (prev?.[n]?.previewUrl) {
        URL.revokeObjectURL(prev[n].previewUrl);
      }

      return {
        ...prev,
        [n]: {
          file,
          previewUrl,
        },
      };
    });

    setEditedRecord((prev) => ({
      ...prev,
      [`Guardian${n}Image`]: previewUrl,
    }));
  };

  const handlePhotoRemove = () => {
    setPhotoError("");
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    setObjectUrl(null);
    setPhotoFile(null);
    if (originalPhotoFilename) {
      setPassportPreview(`${API_UPLOAD_URL}/uploads/${originalPhotoFilename}`);
    } else {
      setPassportPreview(null);
    }
  };

  const getImageUrl = (value) => {
    if (!value) return null;
    if (
      typeof value === "string" &&
      (value.startsWith("blob:") || value.startsWith("http://") || value.startsWith("https://"))
    ) {
      return value;
    }

    return `${API_UPLOAD_URL}/uploads/${value}`;
  };

  const handleSave = async () => {
    if (!editedRecord) return;

    try {
      const hasGuardianImages = Object.keys(guardianImageFiles || {}).length > 0;
      setUploading(!!photoFile || hasGuardianImages);

      let newFilename = null;

      if (photoFile) {
        const fd = new FormData();
        fd.append("CampusCode", logincampusCode);
        fd.append("StudentID", editedRecord.ApplicationID || editedRecord.StudentID);
        fd.append("passportPhoto", photoFile);

        const uploadRes = await axios.post(`${API_URL}/api/uploadStudentPassportPhoto`, fd, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        newFilename = uploadRes.data?.filename;
      }

      const guardianImagePayload = {};

      for (const n of [1, 2, 3]) {
        const guardianObj = guardianImageFiles?.[n];
        const file = guardianObj?.file;

        if (file) {
          const fd = new FormData();
          fd.append("CampusCode", logincampusCode);
          fd.append("StudentID", editedRecord.ApplicationID || editedRecord.StudentID);
          fd.append("guardianNo", n);
          fd.append("guardianImage", file);
        
          const uploadRes = await axios.post(`${API_URL}/api/uploadStudentGuardianImage`, fd, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          });
        
          guardianImagePayload[`Guardian${n}Image`] = uploadRes.data?.filename;
        }
      }

      const payload = {
        ...editedRecord,
        EducationDetails: editedRecord.EducationDetails || [],
        CompetitiveExams: editedRecord.CompetitiveExams || [],
        DocumentDetails: editedRecord.DocumentDetails || [],
        ...(newFilename ? { PassportPhoto: newFilename } : {}),
        ...guardianImagePayload,
      };

      delete payload.Guardian1ImageFileChanged;
      delete payload.Guardian2ImageFileChanged;
      delete payload.Guardian3ImageFileChanged;

      await axios.put(`${API_URL}/api/updateStudentPersonalInformation`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setEditMode(false);
      setData([payload]);

      if (newFilename) {
        setOriginalPhotoFilename(newFilename);
        setPassportPreview(`${API_UPLOAD_URL}/uploads/${newFilename}`);
      }

      setPhotoFile(null);
      Object.values(guardianImageFiles || {}).forEach((x) => {
        if (x?.previewUrl) URL.revokeObjectURL(x.previewUrl);
      });
      setGuardianImageFiles({});

      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
      setPhotoError("");
    } catch (error) {
      console.error("Error saving data:", error);
      setPhotoError("Failed to save. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // IST date (display only)
  const convertToISTDate = (utcTime) =>
    utcTime
      ? new Date(utcTime).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
      : "";

  const FieldGrid = ({ children, count }) => {
    const c = count ?? COLS_XL;
    return (
      <Box
        sx={{
          display: "grid",
          gap: 2,
          alignItems: "start",
          gridAutoFlow: "row dense",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: `repeat(${Math.min(c, 4)}, 1fr)`,
            xl: `repeat(${c}, 1fr)`,
          },
        }}
      >
        {children}
      </Box>
    );
  };

  const handleStudentRowClick = (s) => {
    if (
      isCollapsed &&
      filteredStudents.length === 1 &&
      filteredStudents[0].StudentID === s.StudentID
    ) {
      setFilteredStudents(allFilteredStudents);
      setIsCollapsed(false);
      return;
    }

    setFilteredStudents([s]);
    setIsCollapsed(true);

    setSelectedStudent({
      StudentID: s.StudentID,
      FullName: s.FullName,
      SubCampusCode: s.SubCampusCode,
      DegreeCode: s.DegreeCode,
      BranchCode: s.BranchCode,
      SemesterCode: s.SemesterCode,
      PassportPhoto: s.PassportPhoto, // (safe if undefined)
    });

    setShouldSearch(true);
  };

  // ✅ Section card now uses sectionSx (accent stripe like Marks page)
  const renderSectionCard = (title, fields, count, extraContent = null) => {
    return (
      <Paper key={title} variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
        <Box sx={{ pl: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
              {title}
            </Typography>
            {editMode && (
              <Typography variant="caption" color="text.secondary">
                Editing…
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

          <FieldGrid count={count}>
            {fields.map(([label, fieldName, value]) => (
              <EditableField
                key={`${title}-${fieldName}`}
                label={label}
                fieldName={fieldName}
                value={editMode && editedRecord ? editedRecord[fieldName] : value}
                editMode={editMode}
                onChange={(v) => handleFieldChange(fieldName, v)}
                inputSx={inputSx}
              />
            ))}

            {extraContent &&
              (React.isValidElement(extraContent)
                ? React.cloneElement(extraContent, { key: `${title}-__extra__` })
                : extraContent)}
          </FieldGrid>

          {uploading && <LinearProgress sx={{ mt: 2 }} />}
          {photoError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {photoError}
            </Alert>
          )}
        </Box>
      </Paper>
    );
  };

  const handleArrayFieldChange = (arrayName, index, field, value) => {
    setEditedRecord((prev) => {
      const arr = [...(prev?.[arrayName] || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [arrayName]: arr };
    });
  };
  
  const addArrayRow = (arrayName, row) => {
    setEditedRecord((prev) => ({
      ...prev,
      [arrayName]: [...(prev?.[arrayName] || []), row],
    }));
  };
  
  const removeArrayRow = (arrayName, index) => {
    setEditedRecord((prev) => ({
      ...prev,
      [arrayName]: (prev?.[arrayName] || []).filter((_, i) => i !== index),
    }));
  };
  
  const renderChildEditableSection = (title, arrayName, rows, fields, emptyRow, count) => {
    const activeRows = editMode && editedRecord ? editedRecord[arrayName] || [] : rows || [];
  
    return (
      <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
        <Box sx={{ pl: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
              {title}
            </Typography>
    
            {editMode && (
              <Button
                size="small"
                variant="contained"
                sx={primaryBtnSx}
                onClick={() => addArrayRow(arrayName, emptyRow)}
              >
                Add
              </Button>
            )}
          </Stack>
          
          <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />
          
          {!activeRows.length ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No records found.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {activeRows.map((row, idx) => (
                <Paper
                  key={`${arrayName}-${idx}`}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, borderColor: BORDER }}
                >
                  <FieldGrid count={count}>
                    {fields.map((f) => (
                      <Box key={`${arrayName}-${idx}-${f.key}`}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {f.label}
                        </Typography>
                    
                        {editMode ? (
                          f.type === "checkbox" ? (
                            <input
                              type="checkbox"
                              checked={!!row[f.key]}
                              onChange={(e) =>
                                handleArrayFieldChange(arrayName, idx, f.key, e.target.checked)
                              }
                            />
                          ) : (
                            <TextField
                              size="small"
                              fullWidth
                              sx={inputSx}
                              value={row[f.key] ?? ""}
                              onChange={(e) =>
                                handleArrayFieldChange(arrayName, idx, f.key, e.target.value)
                              }
                            />
                          )
                        ) : (
                          <Typography sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                            {f.type === "checkbox" ? (row[f.key] ? "Yes" : "No") : row[f.key] ?? "-"}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </FieldGrid>
                  
                  {editMode && (
                    <Button
                      size="small"
                      color="error"
                      sx={{ mt: 1, textTransform: "none" }}
                      onClick={() => removeArrayRow(arrayName, idx)}
                    >
                      Remove
                    </Button>
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Information
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter students and click a row to auto-load detailed profile
            </Typography>
          </Box>

          {data.length > 0 &&
            (editMode ? (
              <Stack direction="row" spacing={1} sx={{ width: isSmDown ? "100%" : "auto" }}>
                <Button
                  fullWidth={isSmDown}
                  variant="contained"
                  onClick={handleSave}
                  disabled={uploading}
                  sx={{ ...primaryBtnSx, minWidth: 110 }}
                >
                  {uploading ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Save"}
                </Button>

                <Button
                  fullWidth={isSmDown}
                  variant="outlined"
                  onClick={() => {
                    setEditMode(false);
                    handlePhotoRemove();
                                    
                    Object.values(guardianImageFiles || {}).forEach((x) => {
                      if (x?.previewUrl) URL.revokeObjectURL(x.previewUrl);
                    });
                  
                    setGuardianImageFiles({});
                    setEditedRecord(data[0]);
                  }}
                  disabled={uploading}
                  sx={{ ...outlinedBtnSx, borderColor: "rgba(255,255,255,0.75)", color: "white" }}
                >
                  Cancel
                </Button>
              </Stack>
            ) : (
              <Button
                fullWidth={isSmDown}
                variant="outlined"
                onClick={() => setEditMode(true)}
                sx={{ ...outlinedBtnSx, borderColor: "rgba(255,255,255,0.75)", color: "white" }}
              >
                Edit
              </Button>
            ))}
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Filters Section (accent stripe) */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1,
                  alignItems: "start",
                }}
              >
                <Autocomplete
                  multiple
                  options={options.SubCampusCode}
                  value={filters.SubCampusCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubCampusCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubCampusCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.DegreeCode}
                  value={filters.DegreeCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, DegreeCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="DegreeCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.BranchCode}
                  value={filters.BranchCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, BranchCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="BranchCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.SemesterCode}
                  value={filters.SemesterCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SemesterCode: value.map(String) }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SemesterCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                  <Button
                    variant="contained"
                    onClick={handleLoadStudents}
                    disabled={loadingList}
                    sx={{ ...primaryBtnSx, height: 36 }}
                  >
                    {loadingList ? "Loading…" : "Filter Students"}
                  </Button>

                  {listError && (
                    <Typography variant="body2" sx={{ color: "error.main" }}>
                      {listError}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>

          {loadingList && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
            </Box>
          )}

          {/* ✅ Search Section (surface, no stripe) */}
          <Paper variant="outlined" sx={{ ...surfaceSx, p: 2, mt: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Search (optional)
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            <Stack
              direction={isSmDown ? "column" : "row"}
              spacing={1}
              alignItems={isSmDown ? "stretch" : "center"}
            >
              <Autocomplete
                freeSolo
                fullWidth
                options={studentList}
                getOptionLabel={(option) =>
                  option?.FullName ? `${option.FullName} (${option.StudentID})` : ""
                }
                onInputChange={(_, v) => setSearchTerm(v)}
                onChange={(_, v) => setSelectedStudent(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Search by Name or StudentID" size="small" sx={inputSx} />
                )}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{ ...primaryBtnSx, height: 36, minWidth: 120 }}
              >
                Search
              </Button>
            </Stack>
          </Paper>

          {shouldSearch && data.length === 0 && (
            <Paper variant="outlined" sx={{ ...surfaceSx, p: 3, mt: 2, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                No results found
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Try selecting a student from the list or using Search.
              </Typography>
            </Paper>
          )}

          {/* ✅ Students list table (BLUE header) */}
          {filteredStudents.length > 0 && (
            <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2 }}>
              <Box sx={{ p: 2 }}>
                <Typography sx={{ mb: 1, fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                  Students Matching Filters {isCollapsed ? "(collapsed)" : ""}
                </Typography>

              <Paper elevation={0} sx={tableWrapSx}>
              {/* INNER – ONLY for horizontal scroll */}
              <Box
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
              >
                <TableContainer component="div">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell sx={{ width: 60 }}>#</TableCell>
                        <TableCell>StudentID</TableCell>
                        <TableCell>Full Name</TableCell>
                        <TableCell>SubCampus</TableCell>
                        <TableCell>Degree</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Semester</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredStudents.map((s, idx) => (
                        <TableRow
                          key={`${s.StudentID}-${idx}`}
                          sx={{ ...zebraRowSx(idx), cursor: "pointer" }}
                          hover
                          onClick={() => handleStudentRowClick(s)}
                        >
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{s.StudentID}</TableCell>
                          <TableCell>{s.FullName}</TableCell>
                          <TableCell>{s.SubCampusCode}</TableCell>
                          <TableCell>{s.DegreeCode}</TableCell>
                          <TableCell>{s.BranchCode}</TableCell>
                          <TableCell>{s.SemesterCode}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
                </Paper>
              </Box>
            </Paper>
          )}

          {/* ✅ Drill-down detail cards (accent stripe) */}
          {shouldSearch &&
            data.map((item) => (
              <Box key={item.StudentID} sx={{ mt: 2 }}>
                {renderSectionCard(
                  "Basic Information",
                  [
                    ["Student ID", "StudentID", item.StudentID],
                    ["Full Name", "FullName", item.FullName],
                    ["Aadhar Number", "AadharNumber", item.AadharNumber],
                    ["Admission Date", "AdmissionDate", convertToISTDate(item.AdmissionDate)],
                    ["Academic Year", "AcademicYear", item.AcademicYear],
                    ["Entry", "Entry", item.Entry],
                    ["Qualification", "Qualification", item.Qualification],
                    ["Admission Type", "AdmissionType", item.AdmissionType],
                    ["Hostel Type", "HostelType", item.HostelType],
                    ["Username", "Username", item.Username],
                  ],
                  6,
                  (passportPreview || editMode) && (
                    <Box
                      sx={{
                        gridColumn: { xs: "1 / -1", sm: "1 / -1", lg: "6" },
                        gridRow: { xs: "auto", sm: "auto", lg: "1 / span 2" },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                        alignItems: { xs: "flex-start", md: "flex-end" },
                        gap: 1,
                      }}
                    >
                      {passportPreview ? (
                        <Box
                          component="img"
                          src={passportPreview}
                          alt="Profile"
                          sx={{
                            width: 160,
                            height: 220,
                            borderRadius: 2,
                            objectFit: "cover",
                            border: `1px solid ${BORDER}`,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 160,
                            height: 220,
                            borderRadius: 2,
                            border: `1px dashed ${BORDER}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                          }}
                        >
                          No photo
                        </Box>
                      )}

                      {editMode && (
                        <Stack direction="row" spacing={1}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png"
                            style={{ display: "none" }}
                            onChange={handlePhotoSelect}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handlePickPhoto}
                            disabled={uploading}
                            sx={primaryBtnSx}
                          >
                            Change
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handlePhotoRemove}
                            disabled={uploading}
                            sx={outlinedBtnSx}
                          >
                            Remove
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  )
                )}

                {renderSectionCard(
                  "Personal Information",
                  [
                    ["Date Of Birth", "DateOfBirth", convertToISTDate(item.DateOfBirth)],
                    ["Gender", "Gender", item.Gender],
                    ["Blood Group", "BloodGroup", item.BloodGroup],
                    ["Religion", "Religion", item.Religion],
                    ["Caste", "Caste", item.Caste],
                    ["SubCaste", "SubCaste", item.SubCaste],
                  ],
                  6
                )}

                {renderSectionCard(
                  "Contact Information",
                  [
                    ["Address Line1", "AddressLine1", item.AddressLine1],
                    ["Address Line2", "AddressLine2", item.AddressLine2],
                    ["City", "City", item.City],
                    ["District", "District", item.District],
                    ["State", "State", item.State],
                    ["Zip", "Zip", item.Zip],
                    ["Phone Number", "PhoneNumber", item.PhoneNumber],
                    ["Alternate Phone Number", "AlternatePhoneNumber", item.AlternatePhoneNumber],
                    ["Email Address", "EmailAddress", item.EmailAddress],
                  ],
                  3
                )}

                {renderSectionCard(
                  "Family Information",
                  [
                    ["Father Name", "FatherFullName", item.FatherFullName],
                    ["Father Aadhar", "FatherAadharNumber", item.FatherAadharNumber],
                    ["Father Qualification", "FatherQualification", item.FatherQualification],
                    ["Father Occupation", "FatherOccupation", item.FatherOccupation],
                    ["Father Phone Number", "FatherPhoneNumber", item.FatherPhoneNumber],
                    ["Mother Name", "MotherFullName", item.MotherFullName],
                    ["Mother Aadhar", "MotherAadharNumber", item.MotherAadharNumber],
                    ["Mother Qualification", "MotherQualification", item.MotherQualification],
                    ["Mother Occupation", "MotherOccupation", item.MotherOccupation],
                    ["Mother Phone Number", "MotherPhoneNumber", item.MotherPhoneNumber],
                  ],
                  5
                )}

                {renderSectionCard(
                  "Academic Information",
                  [
                    ["Campus Code", "CampusCode", item.CampusCode],
                    ["SubCampus Code", "SubCampusCode", item.SubCampusCode],
                    ["Degree Code", "DegreeCode", item.DegreeCode],
                    ["Branch Code", "BranchCode", item.BranchCode],
                    ["IsStudentActive", "IsStudentActive", item.IsStudentActive],
                  ],
                  5
                )}

                {renderChildEditableSection(
                  "Education Details",
                  "EducationDetails",
                  item.EducationDetails,
                  [
                    { label: "Level", key: "EducationLevel" },
                    { label: "Board Type", key: "BoardType" },
                    { label: "Institution", key: "InstitutionName" },
                    { label: "Hall Ticket", key: "HallTicketNumber" },
                    { label: "Percentage", key: "Percentage" },
                    { label: "CGPA", key: "CGPA" },
                    { label: "City", key: "InstitutionCity" },
                    { label: "State", key: "InstitutionState" },
                    { label: "Passed Out Year", key: "PassedOutYear" },
                  ],
                  {
                    EducationLevel: "",
                    BoardType: "",
                    InstitutionName: "",
                    HallTicketNumber: "",
                    Percentage: "",
                    CGPA: "",
                    InstitutionCity: "",
                    InstitutionState: "",
                    PassedOutYear: "",
                  },
                  9
                )}

                {renderChildEditableSection(
                  "Competitive / Entrance Exams",
                  "CompetitiveExams",
                  item.CompetitiveExams,
                  [
                    { label: "Exam Type", key: "ExamType" },
                    { label: "Hall Ticket", key: "HallTicketNumber" },
                    { label: "Rank", key: "RankNo" },
                    { label: "Score", key: "Score" },
                    { label: "Year", key: "ExamYear" },
                  ],
                  {
                    ExamType: "",
                    HallTicketNumber: "",
                    RankNo: "",
                    Score: "",
                    ExamYear: "",
                  },
                  5
                )}

                {renderChildEditableSection(
                  "Document Information",
                  "DocumentDetails",
                  item.DocumentDetails,
                  [
                    { label: "Document Type", key: "DocumentType" },
                    { label: "Received", key: "IsReceived", type: "checkbox" },
                    { label: "Verified", key: "IsVerified", type: "checkbox" },
                    { label: "Comments", key: "Comments" },
                  ],
                  {
                    DocumentType: "",
                    IsReceived: false,
                    IsVerified: false,
                    Comments: "",
                  },
                  4
                )}

                {renderSectionCard(
                  "Documents Storage Information",
                  [
                    ["Stored Info", "DocumentsStoredInfo", item.DocumentsStoredInfo],
                    ["Returned To", "DocumentsReturnedTo", item.DocumentsReturnedTo],
                    ["Returned On", "DocumentsReturnedOn", item.DocumentsReturnedOn],
                    ["Verified By", "DocumentsReturnedBy", item.DocumentsReturnedBy],
                  ],
                  4
                )}

                {renderSectionCard(
                  "Reference Information",
                  [
                    ["Reference Name", "ReferenceName", item.ReferenceName],
                    ["Reference Phone Number", "ReferencePhoneNumber", item.ReferencePhoneNumber],
                  ],
                  2
                )}

                {renderSectionCard(
                  "Guardian Information",
                  [
                    ["Guardian 1 Full Name", "Guardian1FullName", item.Guardian1FullName],
                    ["Guardian 2 Full Name", "Guardian2FullName", item.Guardian2FullName],
                    ["Guardian 3 Full Name", "Guardian3FullName", item.Guardian3FullName],
                  ],
                  3,
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(3, 1fr)",
                        },
                      }}
                    >
                      {[1, 2, 3].map((n) => {
                        const imgField = `Guardian${n}Image`;
                        const existingImg =
                          editMode && editedRecord
                            ? editedRecord[imgField]
                            : item[imgField];
                      
                        //const imgUrl = existingImg ? `${API_UPLOAD_URL}/uploads/${existingImg}` : null;
                        const imgUrl = getImageUrl(existingImg);
                        return (
                          <Box key={imgField}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Guardian {n} Image
                            </Typography>
                        
                            {imgUrl ? (
                              <Box
                                component="img"
                                src={imgUrl}
                                alt={`Guardian ${n}`}
                                sx={{
                                  width: 140,
                                  height: 160,
                                  borderRadius: 2,
                                  objectFit: "cover",
                                  border: `1px solid ${BORDER}`,
                                  mb: 1,
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 140,
                                  height: 160,
                                  borderRadius: 2,
                                  border: `1px dashed ${BORDER}`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                  mb: 1,
                                }}
                              >
                                No image
                              </Box>
                            )}

                            {editMode && (
                              <Button
                                component="label"
                                size="small"
                                variant="contained"
                                sx={primaryBtnSx}
                              >
                                Upload
                                <input
                                  hidden
                                  type="file"
                                  accept="image/jpeg,image/png"
                                  onChange={(e) => handleGuardianImageSelect(n, e)}
                                />
                              </Button>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportStudentPersonalInformation;
